import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { selectTheme, selectStyles } from '../../shared/react-select-theme';
import { optionsMap, createOption, mapValueToOptions } from '../../shared/lib';
import { useTranslation } from 'react-i18next';

/**
 * Format list of options for ReactSelect
 * @param {String} writeInValue
 * @param {Array} options
 * @param {Boolean} multiple
 * @param {Boolean} allowCreate
 * @returns {Array} options
 */
const prepareOptions = (writeInValue, options, multiple, allowCreate) => {
  // Create Missing Options for Write-in Values
  if (allowCreate && writeInValue) {
    // Map { "writeInValue" : { Option Ref } }
    const opts = optionsMap(options);
    if (multiple) {
      // ["val","val","val"]
      writeInValue.forEach((v) => {
        if (!opts[v]) {
          options.push(createOption(v));
        }
      });
    } else {
      // "val"
      if (!opts[writeInValue]) {
        options.push(createOption(writeInValue));
      }
    }
  }

  // Return Options
  return options;
};

const handleSetFieldValue = (name, selected, setFieldValue, multiple) =>
  multiple
    ? setFieldValue(name, selected && selected.map((option) => option.value))
    : setFieldValue(name, selected.value);

export default function FormFieldSelect({
  field: { name, value, onChange, onBlur },
  form: { setFieldTouched, setFieldValue },
  question,
  context,
  ...props
}) {
  const { options, multiple, allowCreate } = question.config;
  const [localOptions, setLocalOptions] = useState(
    prepareOptions(value, options, multiple, allowCreate)
  );
  const localValue = mapValueToOptions(value, localOptions, multiple);

  const { t } = useTranslation();

  return (
    (localOptions && (
      <div className="formFieldSelect">
        <Field
          placeholder={multiple ? t('Select all that apply.') : t('Select...')}
          component={allowCreate ? CreatableSelect : Select}
          value={localValue}
          name={name}
          id={name}
          theme={selectTheme}
          styles={selectStyles}
          options={localOptions.map((option) => ({
            ...option,
            label: t(option.label),
          }))}
          isMulti={multiple}
          onBlur={() => setFieldTouched(name, true)}
          onChange={(selected) => {
            handleSetFieldValue(name, selected, setFieldValue, multiple);
          }}
          onCreateOption={(newValue) => {
            const newOption = createOption(newValue);
            setLocalOptions([...localOptions, newOption]);
            handleSetFieldValue(
              name,
              multiple ? [...localValue, newOption] : newOption,
              setFieldValue,
              multiple
            );
          }}
        />
      </div>
    )) ||
    null
  );
}

FormFieldSelect.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  question: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
};
